import jsPDF from "jspdf";
import {
  ContractSectionProps,
  GenInfoProps,
  RunningTotalsProps,
  SoftCostSectionProps,
  ForwardCommitmentSectionProps,
} from "../types";
import moment from "moment";

export type pdfProps = {
  brochurePrice: number;
  builderBonus: number;
  buyer: string;
  buyerSelectedOptions: number;
  cashOptionsPaid: number;
  closingCostBudget: number;
  costOfOptionsWithContract: number;
  customArchitecturalPlanChanges: number;
  dateSold: string;
  discretionary: number;
  exitStrategy: number;
  extraLaborActual: number;
  extraMaterialBudget: number;
  inHouseCommissions: number;
  interimInterestBudget: number;
  jobId: number;
  jobNumber: string;
  laborBudget: number;
  lotCost: number;
  lotExtras: number;
  lotPremium: number;
  materialBudget: number;
  optionalArchitecturalPlanChanges: number;
  optionsGiven: number;
  optionsRetailValue: number;
  planElevationNumber: string;
  planNumber: string;
  preparedBy: string;
  profitAnalysisId: number;
  jobType: string;
  projectId: number;
  projectNumber: string;
  projectedCloseDate: string;
  realtorCommissions: number;
  realtorCommissionsadjusted: number;
  revision: number;
  salesAllowanceClosingCosts: number;
  salesAllowanceOptions: number;
  salesAllowancePriceReduced: number;
  salesPrice: number;
  specialMarketingIncentive: number;
  stageCode: string;
  streetAddress: string;
  subdivision: string;
  specialLabor: number;
  specialMaterial: number;
};

const FONT_HEADING = "helvetica";
const FONT_BODY = "helvetica";
const FONT_MONO = "courier";

const SIZE_TITLE = 16;
const SIZE_SECTION = 11;
const SIZE_LABEL = 7.5;
const SIZE_VALUE = 9.5;
const SIZE_NOTE = 9;

const COLOR_TEXT = 30;
const COLOR_MUTED = 95;
const COLOR_RULE = 185;
const COLOR_SECTION_RULE = 70;

export function generatePDF(
  pdfData: pdfProps,
  genInfo: GenInfoProps,
  contract: ContractSectionProps,
  paType: "spec" | "sold",
  labor: {
    LaborBudget: number | undefined;
    ExtraLaborActual: number | undefined;
    SpecialLabor: number | undefined;
  },
  material: {
    MaterialBudget: number | undefined;
    ExtraMaterialBudget: number | undefined;
    SpecialMaterial: number | undefined;
  },
  lotExtras: {
    LotExtras: number | undefined;
  },
  options: {
    CostOfOptionsWithContract: number | undefined;
  },
  softCost: SoftCostSectionProps,
  forwardCommitment: ForwardCommitmentSectionProps | null,
  totals: RunningTotalsProps,
  notesList: Array<{
    profitAnalysisNoteId: number;
    jobId: number;
    profitAnalysisId: number;
    note: string;
    createdOn: string;
    createdBy: string;
  }>
) {
  const doc = new jsPDF();
  doc.setFont(FONT_BODY, "normal");
  doc.setTextColor(COLOR_TEXT);

  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 10;
  const columnWidth = pageWidth / 2 - margin * 3 + 10;
  const lineHeight = 12;

  const includeForwardCommitmentInPDF = (forwardCommitment != null && typeof forwardCommitment === "object" && forwardCommitment !== undefined && forwardCommitment.HasForwardCommitment != null && forwardCommitment.HasForwardCommitment !== undefined);

  // FIX 5: leave enough room for a full field block (label + value + underline)
  // so rows are never drawn past the bottom edge and lost.
  const checkAndAddNewPage = () => {
    const pageHeight = doc.internal.pageSize.height;
    if (yPos > pageHeight - 30) {
      doc.addPage();
      yPos = 20; // Reset to top of new page with a margin
    }
  };

  // Function to add the notes section
  const addNotes = (_yPos2: number, lineHeight: number) => {

    const leftMargin = 10;
    const pageWidth = doc.internal.pageSize.width;
    const rightMargin = 10;
    const usableWidth = pageWidth - leftMargin - rightMargin;
    const title = "Notes";
    addSectionTitle(title, pageWidth / 2, yPos, "center");
    yPos += lineHeight;

    checkAndAddNewPage();


    const formatDate = (dateString) => {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    };

    notesList.forEach((note) => {
      // Check if we need a new page before adding a note
      checkAndAddNewPage();

      // Author and date line
      const noteText = `${note.createdBy} - ${formatDate(note.createdOn)}`;
      doc.setFontSize(SIZE_NOTE);
      doc.setFont(FONT_HEADING, "bold");
      doc.setTextColor(COLOR_TEXT);
      const lines = doc.splitTextToSize(noteText, usableWidth);
      doc.text(lines, leftMargin, yPos);
      yPos += lines.length * (lineHeight - 5);

      checkAndAddNewPage();

      // Note content
      const noteText2 = `${note.note}`;
      doc.setFont(FONT_BODY, "normal");
      doc.setFontSize(SIZE_NOTE);
      doc.setTextColor(COLOR_TEXT);
      const lines2 = doc.splitTextToSize(noteText2, usableWidth);

      // Check if the upcoming text will fit on the current page
      const textHeight = lines2.length * (lineHeight - 5);
      if (yPos + textHeight > doc.internal.pageSize.height - 20) {
        doc.addPage();
        yPos = 20;
      }

      doc.text(lines2, leftMargin, yPos);
      yPos += textHeight;

      // Add space after each note
      yPos += lineHeight - 5;
      checkAndAddNewPage();
    });
  };

  // Function to add a centered title
  const addTitle = (title: string | string[]) => {
    let y = 10;
    doc.setFont(FONT_HEADING, "bold");
    doc.setFontSize(SIZE_TITLE);
    doc.setTextColor(COLOR_TEXT);
    doc.text(title, pageWidth / 2, y, { align: "center" });

    y += 4;
    doc.setDrawColor(COLOR_SECTION_RULE);
    doc.setLineWidth(0.35);
    doc.line(margin, y, pageWidth - margin, y);

    doc.setFont(FONT_BODY, "normal");
    doc.setFontSize(SIZE_VALUE); // Reset for the rest of the content
    doc.setTextColor(COLOR_TEXT);
  };

  // Helper function to add Forward Commitment section fields
  const addForwardCommitmentField = (
    title: string,
    value: string | number | boolean | undefined
  ) => {
    checkAndAddNewPage();
    const formattedValue =
      typeof value === "number" ? formatCurrency(value) : value;
    addTextField(contractSectionX, yPos, title, formattedValue as any);
    yPos += lineHeight;
  };

  // Helper function to add text fields
  const addTextField = (
    x: number,
    y: number,
    title: string,
    value: string | number,
    fontSize?: number,
    columnWidth: number = pageWidth / 2 - margin * 3 + 10
  ) => {
    const currentFont = doc.getFont() as unknown as { fontName: string; fontStyle: string };

    doc.setFont(FONT_BODY, "normal");
    doc.setFontSize(SIZE_LABEL);
    doc.setTextColor(COLOR_MUTED);
    doc.text(title, x, y, { maxWidth: columnWidth });

    doc.setFont(currentFont.fontName || FONT_BODY, currentFont.fontStyle || "normal");
    doc.setFontSize(fontSize || SIZE_VALUE);
    doc.setTextColor(COLOR_TEXT);
    doc.text(`${value}`, x, y + 5.5, { maxWidth: columnWidth });

    doc.setDrawColor(COLOR_RULE);
    doc.setLineWidth(0.15);
    doc.line(x, y + 7, x + columnWidth, y + 7);
  };


  const addSectionTitle = (title: string, x: number, y: number, align: "left" | "center" = "left") => {
    doc.setFont(FONT_HEADING, "bold");
    doc.setFontSize(SIZE_SECTION);
    doc.setTextColor(COLOR_TEXT);
    doc.text(title, x, y, { align });
    doc.setDrawColor(COLOR_SECTION_RULE);
    doc.setLineWidth(0.25);
    if (align === "center") {
      doc.line(margin, y + 2.5, pageWidth - margin, y + 2.5);
    } else {
      doc.line(x, y + 2.5, x + columnWidth, y + 2.5);
    }
  };

  // FIX 1: formatDate — properly guard sentinel dates (year <= 1 or invalid)
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    if (isNaN(date.getTime()) || date.getFullYear() <= 1) {
      return "";
    }
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  };

  // Vertical position start
  let yPos = 20;

  function percentage(partialValue: number, totalValue: number) {
    return (partialValue / totalValue) * 100;
  }

  const subtotalLaborMaterial =
    (totals.labor || 0) + (totals.material || 0) + (totals.lotExtras || 0);

  const totalLaborMaterial =
    (subtotalLaborMaterial || 0) + (totals.options || 0);

  const totalCost = (totalLaborMaterial || 0) + (totals.softCost || 0);
  const totalCostBP = (totalLaborMaterial || 0) + (totals.softCostBP || 0);
  const totalCostBL = (totalLaborMaterial || 0) + (totals.softCostBL || 0);

  const profitUSD = (totals.contract || 0) - (totalCost || 0);

  const profitMargin = percentage(profitUSD, totals.contract) || 0;

  const varianceToExitStrategy = profitMargin - (genInfo.ExitStrategy || 0);

  // Add the title
  addTitle("Profit Analysis");
  yPos += lineHeight - 5;

  // Start adding fields with reduced spacing
  addTextField(margin, yPos, "Subdivision", genInfo.Subdivision || "");
  addTextField(pageWidth / 2, yPos, "Address", genInfo.StreetAddress || "");

  yPos += lineHeight;
  doc.setFont(FONT_MONO, "normal");
  addTextField(
    margin,
    yPos,
    "Project Number",
    genInfo.ProjectNumber || ""
  );
  addTextField(pageWidth / 3, yPos, "Job Number", genInfo.JobNumber || "", SIZE_VALUE, pageWidth / 3 - 50);
  addTextField(
    pageWidth / 2,
    yPos,
    "Stage Code",
    genInfo.StageCode || ""
  );
  doc.setFont(FONT_BODY, "normal");

  yPos += lineHeight;
  addTextField(margin, yPos, "Buyer", genInfo.Buyer || "", SIZE_VALUE);
  const planAndElevation = `${genInfo.PlanNumber || ""} / ${genInfo.PlanElevationNumber || ""
    }`;
  addTextField(pageWidth / 2, yPos, "Plan / Elevation", planAndElevation || "");

  yPos += lineHeight;
  // FIX 6: guard undefined so the value never renders as "undefined%"
  addTextField(margin, yPos, "Job Type", genInfo.JobType || "");
  addTextField(
    pageWidth / 2,
    yPos,
    "Exit Strategy",
    `${genInfo.ExitStrategy ?? 0}%`
  );

  // FIX 7: spec-only BP % / BL % inputs (mirrors the Job Info form)
  if (paType === "spec") {
    yPos += lineHeight;
    addTextField(margin, yPos, "BP %", `${genInfo.BPPercent ?? 0}%`);
    addTextField(pageWidth / 2, yPos, "BL %", `${genInfo.BLPercent ?? 0}%`);
  }

  yPos += lineHeight;
  // FIX 2: only render Date Sold when it's a real date (spec jobs have no date sold)
  const dateSoldFormatted = formatDate(pdfData.dateSold || "");
  if (dateSoldFormatted) {
    addTextField(margin, yPos, "Date Sold", dateSoldFormatted);
  }
  addTextField(
    pageWidth / 2,
    yPos,
    "Projected Closed Date",
    formatDate(genInfo.ProjectedCloseDate?.toString() || "") || ""
  );

  yPos += lineHeight;
  checkAndAddNewPage();

  // Helper function to format currency
  const formatCurrency = (amount: number | undefined, isNegative?: boolean) =>
    amount !== undefined
      ? `$ ${isNegative ? "- " : ""}${amount.toLocaleString("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })}`
      : "$ 0";

  // Helper function to format percentages
  const formatPercentage = (amount: number | undefined) =>
    amount !== undefined
      ? `${amount.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })} %`
      : "$ 0";

  // New x position for the right-aligned contract section
  const contractSectionX = pageWidth - columnWidth - margin * 2;
  yPos += lineHeight - 5;

  const isBoldTitle = (title: string) =>
    title === "Contract Price" ||
    title === "Full Retail" ||
    title === "Total Cost" ||
    title === "Profit USD" ||
    title === "Profit Margin" ||
    title === "Variance To Exit Strategy";

  // Helper function to add contract section fields
  const addContractField = (
    title: string,
    value: number | undefined,
    isTotal = false
  ) => {
    checkAndAddNewPage(); // FIX 5: never draw a row past the bottom edge
    const formattedValue = formatCurrency(value);

    if (isBoldTitle(title)) doc.setFont(FONT_HEADING, "bold");

    addTextField(
      isTotal ? contractSectionX + 1 : contractSectionX,
      yPos,
      title,
      formattedValue
    );

    if (isBoldTitle(title)) doc.setFont(FONT_BODY, "normal");

    yPos += lineHeight;
  };

  // Helper function to add negative contract section fields
  const addNegativeContractField = (
    title: string,
    value: number | undefined,
    isTotal = false
  ) => {
    checkAndAddNewPage();
    const formattedValue = formatCurrency(value);

    if (isBoldTitle(title)) doc.setFont(FONT_HEADING, "bold");

    addTextField(
      isTotal ? contractSectionX + 1 : contractSectionX,
      yPos,
      title,
      `-${formattedValue}`
    );

    if (isBoldTitle(title)) doc.setFont(FONT_BODY, "normal");

    yPos += lineHeight;
  };

  const addPercentageField = (
    title: string,
    value: number | undefined,
    isTotal = false
  ) => {
    checkAndAddNewPage();
    const formattedValue = formatPercentage(value);

    if (isBoldTitle(title)) doc.setFont(FONT_HEADING, "bold");

    addTextField(
      isTotal ? contractSectionX + 1 : contractSectionX,
      yPos,
      title,
      formattedValue
    );

    if (isBoldTitle(title)) doc.setFont(FONT_BODY, "normal");

    yPos += lineHeight;
  };

  // Reset font to normal for other fields
  doc.setFont(FONT_BODY, "normal");

  // Add a bold title for the contract section
  addSectionTitle("Contract", contractSectionX, yPos);
  yPos += lineHeight;

  doc.setFont(FONT_BODY, "normal");

  // Fields shown for both spec and sold (mirrors ContractSection)
  addContractField("Brochure Price", contract.BrochurePrice || 0);
  addContractField("Lot Premium Price", contract.LotPremium || 0);
  addContractField(
    "Optional Architectural Plan Changes",
    contract.OptionalArchitecturalPlanChanges || 0
  );
  addContractField(
    "Buyer Selected Options/Spec Options",
    contract.BuyerSelectedOptions || 0
  );

  // FIX 8: sold-only contract fields (the spec UI never renders these)
  if (paType === "sold") {
    addContractField(
      "Custom Architectural Plan Changes",
      contract.CustomArchitecturalPlanChanges || 0
    );
    addContractField(
      "Options Given TBD/Seller Paid Closing Costs",
      contract.OptionsGiven || 0
    );
    addContractField(
      "Options Retail Value @ N/C - (Incl Spec Opts)",
      contract.OptionsRetailValue || 0
    );
    addContractField("Cash Options Paid", contract.CashOptionsPaid || 0);
    addNegativeContractField(
      "Sales Allowance Options",
      contract.SalesAllowanceOptions || 0
    );
    addNegativeContractField(
      "Sales Allowance Closing Costs",
      contract.SalesAllowanceClosingCosts || 0
    );
    addNegativeContractField(
      "Sales Allowance Price Reduced",
      contract.SalesAllowancePriceReduced || 0
    );
    addContractField("Contract Price", totals.contract || 0, true);
  }

  // FIX 9: spec-only summary rows (mirrors ContractSection's spec block)
  if (paType === "spec") {
    addContractField("Full Retail", totals.fullRetail || 0, true);
    addContractField("Discount to BP", totals.discountToBP || 0);
    addContractField("BP Price", totals.bpPrice || 0);
    addContractField("Discount to BL", totals.discountToBL || 0);
    addContractField("BL Price", totals.blPrice || 0);
  }

  yPos += 10;
  checkAndAddNewPage();

  // Add a bold title for the labor section
  addSectionTitle("Labor", contractSectionX, yPos);
  yPos += lineHeight;

  doc.setFont(FONT_BODY, "normal");

  // Helper function to add labor section fields
  const addLaborField = (title: string, value: number | undefined) => {
    checkAndAddNewPage();
    addTextField(contractSectionX, yPos, title, formatCurrency(value));
    yPos += lineHeight;
  };

  // Add fields for the labor section
  addLaborField("Labor Budget/Category A", labor.LaborBudget || 0);
  addLaborField("Extra Labor Actual/Category B", labor.ExtraLaborActual || 0);
  addLaborField("Special Labor Budget", labor.SpecialLabor || 0);

  // Total Labor
  checkAndAddNewPage();
  const totalLaborValue = formatCurrency(totals.labor || 0);
  doc.setFont(FONT_HEADING, "bold");
  addTextField(contractSectionX + 1, yPos, "Total Labor", totalLaborValue || 0);
  doc.setFont(FONT_BODY, "normal");
  doc.setFontSize(SIZE_VALUE);
  doc.setLineWidth(0.2);
  yPos += lineHeight;
  checkAndAddNewPage();

  yPos += lineHeight;

  // Add a bold title for the material section
  addSectionTitle("Material", contractSectionX, yPos);
  yPos += lineHeight;

  doc.setFont(FONT_BODY, "normal");

  // Helper function to add material section fields
  const addMaterialField = (title: string, value: number | undefined) => {
    checkAndAddNewPage();
    addTextField(contractSectionX, yPos, title, formatCurrency(value));
    yPos += lineHeight;
  };

  // Add fields for the material section
  addMaterialField("Material Budget/Category C", material.MaterialBudget || 0);
  addMaterialField(
    "Extra Material Budget/Category D",
    material.ExtraMaterialBudget || 0
  );
  addMaterialField("Special Material Budget", material.SpecialMaterial || 0);

  // Total Material
  checkAndAddNewPage();
  const totalMaterialValue = formatCurrency(totals.material || 0);
  doc.setFont(FONT_HEADING, "bold");
  addTextField(
    contractSectionX + 1,
    yPos,
    "Total Material",
    totalMaterialValue
  );
  doc.setFont(FONT_BODY, "normal");
  doc.setFontSize(SIZE_VALUE);
  doc.setLineWidth(0.2);
  yPos += lineHeight;
  checkAndAddNewPage();

  yPos += lineHeight;

  // Add a bold title for the lot extras section
  addSectionTitle("Lot Extras(Exit Strategy)", contractSectionX, yPos);
  yPos += lineHeight;

  doc.setFont(FONT_BODY, "normal");

  // Helper function to add lot extras section fields
  const addLotExtrasField = (title: string, value: number | undefined) => {
    checkAndAddNewPage();
    addTextField(contractSectionX, yPos, title, formatCurrency(value));
    yPos += lineHeight;
  };

  // Add fields for the lot extras section
  addLotExtrasField("Lot Extras(Exit Strategy)", lotExtras.LotExtras || 0);

  // Total Lot Extras
  checkAndAddNewPage();
  const totalLotExtrasValue = formatCurrency(totals.lotExtras || 0);
  doc.setFont(FONT_HEADING, "bold");
  addTextField(
    contractSectionX + 1,
    yPos,
    "Total Lot Extras(Exit Strategy)",
    totalLotExtrasValue
  );
  doc.setFont(FONT_BODY, "normal");
  doc.setFontSize(SIZE_VALUE);
  doc.setLineWidth(0.2);
  yPos += lineHeight;
  checkAndAddNewPage();

  doc.setFont(FONT_HEADING, "bold");
  const totalLaborMaterialValue = formatCurrency(subtotalLaborMaterial || 0);
  addTextField(
    contractSectionX + 1,
    yPos,
    "Subtotal Labor & Material",
    totalLaborMaterialValue
  );
  doc.setFont(FONT_BODY, "normal");
  doc.setFontSize(SIZE_VALUE);
  doc.setLineWidth(0.2);
  yPos += lineHeight;
  checkAndAddNewPage();

  yPos += lineHeight;

  // Add a bold title for the options section
  addSectionTitle("Options", contractSectionX, yPos);
  yPos += lineHeight;

  doc.setFont(FONT_BODY, "normal");

  // Helper function to add options section fields
  const addOptionsField = (title: string, value: number | undefined) => {
    checkAndAddNewPage();
    addTextField(contractSectionX, yPos, title, formatCurrency(value));
    yPos += lineHeight;
  };

  // Add fields for the options section
  addOptionsField(
    "Cost of Options with Contract",
    options.CostOfOptionsWithContract || 0
  );

  checkAndAddNewPage();
  doc.setFont(FONT_HEADING, "bold");
  const totalOptionsValue = formatCurrency(totals.options);
  addTextField(
    contractSectionX + 1,
    yPos,
    "Cost of Total Options",
    totalOptionsValue
  );
  doc.setFont(FONT_BODY, "normal");
  doc.setFontSize(SIZE_VALUE);
  doc.setLineWidth(0.2);
  yPos += lineHeight;
  checkAndAddNewPage();

  doc.setFont(FONT_HEADING, "bold");
  const totalLaborAndMaterialValue = formatCurrency(totalLaborMaterial || 0);
  addTextField(
    contractSectionX + 1,
    yPos,
    "Total Labor & Materials",
    totalLaborAndMaterialValue
  );
  doc.setFont(FONT_BODY, "normal");
  doc.setFontSize(SIZE_VALUE);
  doc.setLineWidth(0.2);
  yPos += lineHeight;
  checkAndAddNewPage();

  yPos += lineHeight;

  checkAndAddNewPage();

  // Add a bold title for the Soft Cost section
  addSectionTitle("Soft Cost", contractSectionX, yPos);
  yPos += lineHeight;

  checkAndAddNewPage();

  doc.setFont(FONT_BODY, "normal");

  // Helper function to add soft cost section fields
  const addSoftCostField = (
    title: string,
    value: string | number | boolean | undefined
  ) => {
    checkAndAddNewPage();
    const formattedValue =
      typeof value === "number" ? formatCurrency(value) : value;
    addTextField(contractSectionX, yPos, title, formattedValue as any);
    yPos += lineHeight;
  };

  // Helper function to add negative soft cost section fields
  const addNegativeSoftCostField = (
    title: string,
    value: string | number | undefined
  ) => {
    checkAndAddNewPage();
    const formattedValue =
      typeof value === "number" ? formatCurrency(value) : value;
    addTextField(contractSectionX, yPos, title, `-${formattedValue}` as any);
    yPos += lineHeight;
  };

  // Add fields for the soft cost section
  addSoftCostField(
    "Lot Cost/(Code F)(Note 2)(+/1 Deviation)",
    softCost.LotCost || 0
  );
  addSoftCostField(
    "Discretionary - Buyer Closing Costs paid by Seller",
    softCost.Discretionary || 0
  );
  addSoftCostField("Has Realtor", softCost.HasRealtor ? "Yes" : "No");
  // FIX 10: spec shows the BL/BP commission split (MoneyField2 in the UI),
  // fed from running totals; sold keeps the single editable value.
  if (paType === "spec") {
    addSoftCostField(
      "Realtor Commissions-(3%)/(Category H) - BL",
      totals.blRealtorCommissions || 0
    );
    addSoftCostField(
      "Realtor Commissions-(3%)/(Category H) - BP",
      totals.bpRealtorCommissions || 0
    );
  } else {
    addSoftCostField(
      "Realtor Commissions-(3%)/(Category H)",
      softCost.RealtorCommissions || 0
    );
  }
  addSoftCostField(
    "Market Adjustment",
    softCost.SpecialMarketingIncentive || 0
  );
  // FIX 3: only negate Realtor Commissions Adjusted when non-zero
  if ((softCost.RealtorCommissionsadjusted || 0) !== 0) {
    addNegativeSoftCostField(
      "Realtor Commissions Adjusted",
      softCost.RealtorCommissionsadjusted || 0
    );
  } else {
    addSoftCostField("Realtor Commissions Adjusted", 0);
  }
  // FIX 4: use pdfData for fields that diverge between live state and saved snapshot
  if (paType === "spec") {
    addSoftCostField(
      "In-House Commissions-(2%)/(Category I) - BL",
      totals.blInHouseCommissions || 0
    );
    addSoftCostField(
      "In-House Commissions-(2%)/(Category I) - BP",
      totals.bpInHouseCommissions || 0
    );
  } else {
    addSoftCostField(
      "In-House Commissions-(2%)/(Category I)",
      pdfData.inHouseCommissions || 0
    );
  }
  addSoftCostField(
    "Interim Interest Budget(#8140)",
    softCost.InterimInterestBudget || 0
  );
  addSoftCostField(
    "Closing Cost Budget/(Category K)",
    softCost.ClosingCostBudget || 0
  );

  if (paType === "spec") {
    addSoftCostField("Builder Bonus (#8195) - BL", totals.blBuilderBonus || 0);
    addSoftCostField("Builder Bonus (#8195) - BP", totals.bpBuilderBonus || 0);
  } else {
    addSoftCostField("Builder Bonus (#8195)", pdfData.builderBonus || 0);
  }

  checkAndAddNewPage();
  doc.setFont(FONT_HEADING, "bold");
  const totalSoftCostValue = formatCurrency(totals.softCost || 0);
  const totalSoftCostValueBP = formatCurrency(totals.softCostBP || 0);
  const totalSoftCostValueBL = formatCurrency(totals.softCostBL || 0);

  if (paType === "spec") {
    addTextField(contractSectionX + 1, yPos, "Subtotal Soft Cost - BP", totalSoftCostValueBP);
    yPos += lineHeight;
    addTextField(contractSectionX + 1, yPos, "Subtotal Soft Cost - BL", totalSoftCostValueBL);
  } else {
    addTextField(contractSectionX + 1, yPos, "Subtotal Soft Cost", totalSoftCostValue);
  }


  yPos += lineHeight;
  yPos += lineHeight;
  checkAndAddNewPage();

  // Add fields for the Forward Commitment section
  if (includeForwardCommitmentInPDF) {

    addSectionTitle("Forward Commitment", contractSectionX, yPos);
    yPos += lineHeight;

    checkAndAddNewPage();

    doc.setFont(FONT_BODY, "normal");

    addForwardCommitmentField(
      "Has Forward Commitment",
      forwardCommitment.HasForwardCommitment ? "Yes" : "No"
    );

    addForwardCommitmentField(
      "Forward Commitment Expiry Date",
      forwardCommitment.ForwardCommitmentExpiryDate
        ? moment(forwardCommitment.ForwardCommitmentExpiryDate).format("YYYY-MM-DD")
        : "N/A"
    );

    addForwardCommitmentField(
      "Forward Commitment Batch Number",
      forwardCommitment.ForwardCommitmentBatchNumber || "N/A"
    );
  }

  checkAndAddNewPage();

  doc.setFont(FONT_BODY, "normal");
  doc.setFontSize(SIZE_VALUE);
  doc.setLineWidth(0.2);
  yPos += lineHeight;
  checkAndAddNewPage();
  yPos += lineHeight;

  // Summary — mirrors the KPI sidebar per paType
  addSectionTitle("Summary", contractSectionX, yPos);
  yPos += lineHeight;
  doc.setFont(FONT_BODY, "normal");
  if (paType === "spec") {
    // Spec sidebar: Full Retail hero, Total Cost, Exit / BP / BL groups
    addContractField("Full Retail", totals.contract || 0);
    addContractField("Total Cost - BP", totalCostBP || 0);
    addContractField("Total Cost - BL", totalCostBL || 0);
    addPercentageField("Exit %", totals.exitPercent || 0);
    addContractField("Exit Price", totals.exitPrice || 0);
    addContractField("Exit Profit", totals.exitProfit || 0);
    addPercentageField("BP %", totals.bpPercent || 0);
    addContractField("BP Price", totals.bpPrice || 0);
    addContractField("BP Profit", totals.bpProfit || 0);
    addPercentageField("BL %", totals.blPercent || 0);
    addContractField("BL Price", totals.blPrice || 0);
    addContractField("BL Profit", totals.blProfit || 0);
  } else {
    addContractField("Contract Price", totals.contract || 0);
    addContractField("Total Cost", totalCost || 0);
    addContractField("Profit USD", profitUSD || 0);
    addPercentageField("Profit Margin", profitMargin || 0);
    addPercentageField("Variance To Exit Strategy", varianceToExitStrategy || 0);
  }

  yPos += lineHeight;
  checkAndAddNewPage();

  addNotes(yPos, lineHeight); // Add the notes section
  // Save the PDF
  doc.save(`${genInfo.StreetAddress}`);
}
